import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import styles from './Toast.module.css';

/**
 * Toast notification component.
 *
 * Displays a temporary notification message that auto-dismisses.
 *
 * @param {Object} props
 * @param {string} props.message - Toast message text
 * @param {'success' | 'error' | 'warning' | 'info'} [props.type='info'] - Toast type
 * @param {number} [props.duration=3000] - Auto-dismiss duration in ms (0 = no auto-dismiss)
 * @param {Function} [props.onClose] - Called when toast is dismissed
 * @param {string} [props.className] - Additional CSS classes
 *
 * @example
 * <Toast message="Photo saved" type="success" onClose={() => setToast(null)} />
 */
export default function Toast({
  message,
  type = 'info',
  duration = 3000,
  onClose,
  className,
}) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onClose?.(), 200); // Wait for exit animation
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose?.(), 200);
  };

  if (!message) return null;

  return createPortal(
    <div
      className={clsx(
        styles.toast,
        styles[type],
        !isVisible && styles.exiting,
        className
      )}
      role="alert"
      aria-live="polite"
    >
      <span className={styles.message}>{message}</span>
      <button
        className={styles.closeButton}
        onClick={handleClose}
        aria-label="Dismiss notification"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>,
    document.body
  );
}

/**
 * Hook for managing toast state.
 *
 * @example
 * const { toast, showToast, hideToast } = useToast();
 *
 * showToast('Saved!', 'success');
 *
 * {toast && <Toast {...toast} onClose={hideToast} />}
 */
export function useToast() {
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'info', duration = 3000) => {
    setToast({ message, type, duration });
  };

  const hideToast = () => {
    setToast(null);
  };

  return { toast, showToast, hideToast };
}
